import {
  SENTRY_MAX_BODY_LENGTH,
  SENTRY_SCRUB_FIELDS,
  SENTRY_SCRUB_MARKER,
} from './sentry-constants';

const SCRUB_FIELD_SET = new Set<string>(SENTRY_SCRUB_FIELDS.map((field) => field.toLowerCase()));
const TOKEN_QUERY_PATTERN = new RegExp(
  `(${SENTRY_SCRUB_FIELDS.join('|')})=([^&\\s]+)`,
  'gi'
);

export const sanitizeUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  try {
    const parsed = new URL(trimmed, window.location.origin);
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    const withoutQuery = trimmed.split('?')[0];
    return withoutQuery.split('#')[0];
  }
};

export const sanitizeHeaders = (headers: Record<string, string>): Record<string, string> => {
  const sanitized: Record<string, string> = {};
  Object.keys(headers).forEach((key) => {
    const lower = key.toLowerCase();
    if (SCRUB_FIELD_SET.has(lower)) return;
    sanitized[key] = headers[key];
  });
  return sanitized;
};

export const sanitizeValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    if (trimmed.length > SENTRY_MAX_BODY_LENGTH) {
      return `${SENTRY_SCRUB_MARKER} (len=${trimmed.length})`;
    }
    return trimmed.replace(TOKEN_QUERY_PATTERN, `$1=${SENTRY_SCRUB_MARKER}`);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (isPlainObject(value)) {
    const sanitized: Record<string, unknown> = {};
    Object.keys(value).forEach((key) => {
      if (SCRUB_FIELD_SET.has(key.toLowerCase())) {
        sanitized[key] = SENTRY_SCRUB_MARKER;
      } else {
        sanitized[key] = sanitizeValue(value[key]);
      }
    });
    return sanitized;
  }

  return value;
};

export const sanitizeRequestData = (data: unknown): unknown => {
  if (data == null) return data;
  if (typeof data === 'string') {
    return sanitizeValue(data);
  }

  try {
    const serialized = JSON.stringify(data);
    if (serialized.length > SENTRY_MAX_BODY_LENGTH) {
      return `${SENTRY_SCRUB_MARKER} (len=${serialized.length})`;
    }
  } catch {
    // ignore serialization issues
  }

  return sanitizeValue(data);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};
